import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../../../environments/environment';
import { ACCESS_TOKEN_SPLIT_THRESHOLD, StorageToken } from '../../../../shared/configs/storage-token.config';
import { API_ENDPOINTS } from '../../../../shared/utils/api-endpoints';
import { StorageService } from '../../storage/storage.service';
import { ApiService } from '../api.service';
import { UserService } from './../../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ApiService {
  private endpoint = API_ENDPOINTS.AUTH;
  private channel = new BroadcastChannel('logout-channel');

  /** De-dupes concurrent refresh attempts (e.g. several 401s at once) into a single token request. */
  private refreshInFlight: Promise<boolean> | null = null;



  private refreshCookieOptions(): any {
    return {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
    };
  }

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private userService: UserService,
  ) {
    super(http);
  }

  get isLoggedIn(): boolean {
    return !!this.userService.userDetails;
  }

  get accessToken(): string {
    if (this.storageService.getCookie('tokenSplit') === 'true') {
      const part1 = this.storageService.getCookie('access_token_1');
      const part2 = this.storageService.getCookie('access_token_2');

      return part1 && part2 ? part1 + part2 : '';
    }

    return this.storageService.getCookie(StorageToken.accessToken) || '';
  }


  private setAccessTokenCookie(accessToken: string, expiresInSeconds: number): void {
    const expires = new Date();
    expires.setSeconds(expires.getSeconds() + (expiresInSeconds || 3600));
    const options: any = {
      expires,
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
    };

    this.storageService.deleteCookie(StorageToken.accessToken, '/');
    this.storageService.deleteCookie('access_token_1', '/');
    this.storageService.deleteCookie('access_token_2', '/');
    this.storageService.deleteCookie('tokenSplit', '/');

    if (accessToken.length > ACCESS_TOKEN_SPLIT_THRESHOLD) {
      const mid = Math.ceil(accessToken.length / 2);
      this.storageService.addCookie('access_token_1', accessToken.slice(0, mid), options);
      this.storageService.addCookie('access_token_2', accessToken.slice(mid), options);
      this.storageService.addCookie('tokenSplit', 'true', options);
    } else {
      this.storageService.addCookie(StorageToken.accessToken, accessToken, options);
    }
  }

  /**
   * The app's own base URL for the CURRENT host (origin + <base href>):
   *   local    -> http://localhost:4200/dev-bo
   *   deployed -> https://dev-auth.nephroplus.com/bo (uat-bo / nephroplus.com / etc.)
   * Used as the Cognito redirect_uri so login returns to wherever you started.
   * NOTE: the matching URL must be registered in the Cognito app client's Allowed callback URLs.
   */
  private getAppBaseUrl(): string {
    const baseHref = document.querySelector('base')?.getAttribute('href') || '/';

    return window.location.origin + baseHref.replace(/\/+$/, '');
  }

  login() {
    const { domain, clientId, scope, responseType } = environment.cognito;
    const redirectUri = this.getAppBaseUrl();
    const cognitoLoginUrl =
      `https://${domain}/login` +
      `?client_id=${clientId}` +
      `&response_type=${responseType}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = cognitoLoginUrl;
  }

  handleCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return Promise.resolve();

    const { domain, clientId } = environment.cognito;
    const tokenUrl = `https://${domain}/oauth2/token`;
    // redirect_uri here MUST match the one used in login().
    const appBaseUrl = this.getAppBaseUrl();

    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('redirect_uri', appBaseUrl)
      .set('client_id', clientId);

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http
      .post<any>(tokenUrl, body.toString(), { headers })
      .toPromise()
      .then((tokens: any) => {
        if (tokens?.access_token) {
          this.setAccessTokenCookie(tokens.access_token, tokens.expires_in);
        }
        // Cognito returns a refresh_token here. Keep it so we can get a new access token without
        // sending the user back through the Cognito login. Its cookie lifetime follows the Cognito
        // app client's "Refresh token expiration" via refreshCookieOptions() (config-driven, not a
        // hardcoded FE value).
        if (tokens?.refresh_token) {
          this.storageService.addCookie('refresh_token', tokens.refresh_token, this.refreshCookieOptions());
        }
        
        window.location.replace(appBaseUrl + '/');
      })
      .catch((err: any) => {
        // Surface WHY the token exchange failed (this is what causes the re-login loop).
        // err.error usually contains Cognito's { "error": "invalid_client" | "invalid_grant" | ... }.
        // eslint-disable-next-line no-console
        console.error('Cognito /oauth2/token exchange FAILED:', err?.status, err?.error || err?.message || err);
        // Do NOT redirect back to the app here — that re-triggers the guard -> login() -> Cognito
        // "Sign in as X" loop. Leave the page so the error above is visible in the console.
      });
  }

  /**
   * Gets a new access token from Cognito using the stored refresh_token (no user interaction).
   * Cognito returns a new access_token + id_token (it does NOT return a new refresh_token).
   * Resolves to true if refreshed & stored, false otherwise (e.g. refresh token expired/revoked).
   */
  refreshAccessToken(): Promise<boolean> {
    // Collapse parallel callers (e.g. a burst of 401s) onto one in-flight request.
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refreshToken = this.storageService.getCookie('refresh_token');
    if (!refreshToken) {
      return Promise.resolve(false);
    }

    const { domain, clientId } = environment.cognito;
    const tokenUrl = `https://${domain}/oauth2/token`;
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken)
      .set('client_id', clientId);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const inFlight: Promise<boolean> = this.http
      .post<any>(tokenUrl, body.toString(), { headers })
      .toPromise()
      .then((tokens: any) => {
        if (!tokens?.access_token) {
          return false;
        }
        this.setAccessTokenCookie(tokens.access_token, tokens.expires_in);

        return true;
      })

      .catch(() => false)
      .then((ok: boolean) => {
        this.refreshInFlight = null;

        return ok;
      });

    this.refreshInFlight = inFlight;

    return inFlight;
  }

  /**
   * Ensures a usable access token before the app boots. If the access-token cookie has expired
   * but a refresh_token is still present, silently mint a new access token (no Cognito redirect).
   * Resolves true if an access token is available afterwards. Called from the app initializer so a
   * page refresh after token expiry renews the session instead of bouncing through Cognito login.
   */
  ensureValidAccessToken(): Promise<boolean> {
    if (this.accessToken) {

      return Promise.resolve(true);
    }
    if (!this.storageService.getCookie('refresh_token')) {
      return Promise.resolve(false);
    }
    
    return this.refreshAccessToken();
  }

  logout() {
    this.logoutLocally();
  }

  logoutLocally(): void {
    const { domain, clientId } = environment.cognito;
    this.storageService.clearCookies(['/', window.location.origin]);
    this.clearAllCookies();
    localStorage.removeItem('app_tab_lock');
    // Clear the cached permission token so the next login re-fetches fresh. It has no time expiry,
    // so logout is what ends its lifecycle.
    localStorage.removeItem('np_permission_token');
    sessionStorage.removeItem('np_permission_token'); // legacy key cleanup
    localStorage.removeItem('np_permissions'); // legacy key cleanup
    // Return to wherever we logged out from (local -> localhost, deployed -> that host), matching
    // login()'s dynamic redirect_uri. This URL must be in the Cognito app client's Allowed sign-out URLs.
    const logoutRedirect = this.getAppBaseUrl();
    const cognitoLogoutUrl =
      `https://${domain}/logout` +
      `?client_id=${clientId}` +
      `&logout_uri=${encodeURIComponent(logoutRedirect)}`;
    window.location.href = cognitoLogoutUrl;
  }

  clearAllCookies() {
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;

      if (
        [
          StorageToken.masquerade.trim(),
          StorageToken.previousAccessToken.trim(),
          'previous_access_token_1',
          'previous_access_token_2',
          StorageToken.masRefreshToken.trim(),
          StorageToken.masqueradePermissions.trim(),
          StorageToken.masAccessToken.trim(),
          StorageToken.accessToken.trim(),
          // Cognito refresh + split-token cookies. Without clearing refresh_token, the app-initializer
          // would silently re-mint an access token on the next load and log the user back in.
          'refresh_token',
          'tokenSplit',
          'access_token_1',
          'access_token_2',
        ].includes(name.trim())
      ) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }
    this.storageService.deleteFromLocalStorage(StorageToken.masqueradePermissions);
  }

  sendLogoutSignal() {
    this.channel.postMessage('logout');
  }
}
